import { CellAlign, HeadCell } from '@dfl/mui-admin-layout';
import { Chip, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createdATColumn } from 'modules/common/constants';
import { IAutoAdjustmentRule, IProviderRef } from '../interfaces/IAutoAdjustmentRule';
import { AutoAdjustmentRuleActions } from '../components/AutoAdjustmentRuleActions';

const SourceMetricChip = ({ value }: { value: string }) => {
  const { t } = useTranslation('auto-adjustment-rules');
  return (
    <Chip
      size={'small'}
      color={'primary'}
      label={t(`sourceMetric.${value}`)}
    />
  );
};

const ActiveStatusChip = ({ value }: { value: boolean }) => {
  const { t } = useTranslation('auto-adjustment-rules');
  return (
    <Chip
      size={'small'}
      color={value ? 'success' : 'default'}
      label={t(value ? 'status.ACTIVE' : 'status.INACTIVE')}
    />
  );
};

export const sourceMetricColumn: HeadCell<IAutoAdjustmentRule> = {
  field: 'sourceMetric',
  headerName: 'auto-adjustment-rules:fields.sourceMetric',
  translate: true,
  renderCell: (sourceMetric: string) => <SourceMetricChip value={sourceMetric} />,
};

export const fromProviderColumn: HeadCell<IAutoAdjustmentRule> = {
  field: 'fromProvider',
  headerName: 'auto-adjustment-rules:fields.fromProvider',
  renderCell: (fromProvider: IProviderRef) => {
    if (!fromProvider) return '-';
    return fromProvider?.name ?? '-';
  },
};

export const toProviderColumn: HeadCell<IAutoAdjustmentRule> = {
  field: 'toProvider',
  headerName: 'auto-adjustment-rules:fields.toProvider',
  renderCell: (toProvider: IProviderRef) => {
    if (!toProvider) return '-';
    return toProvider?.name ?? '-';
  },
};

export const percentageColumn: HeadCell<IAutoAdjustmentRule> = {
  field: 'percentage',
  headerName: 'auto-adjustment-rules:fields.percentage',
  align: CellAlign.CENTER,
  renderCell: (percentage: number) => {
    if (percentage === null || percentage === undefined) return '-';
    return `${percentage}%`;
  },
};

const SupplierScopeCell = ({ value }: { value: any }) => {
  const { t } = useTranslation('auto-adjustment-rules');
  if (!value || value.mode !== 'SPECIFIC' || !value.suppliers?.length) {
    return <Chip size={'small'} label={t('supplierScope.mode.ALL')} />;
  }
  const names = value.suppliers.map((s: any) => s.name).join(', ');
  return (
    <Tooltip title={names}>
      <Chip
        size={'small'}
        color={'info'}
        label={t('supplierScope.count', { count: value.suppliers.length })}
      />
    </Tooltip>
  );
};

export const supplierScopeColumn: HeadCell<IAutoAdjustmentRule> = {
  field: 'supplierScope',
  headerName: 'auto-adjustment-rules:fields.supplierScope',
  align: CellAlign.CENTER,
  sortable: false,
  renderCell: (supplierScope: any) => <SupplierScopeCell value={supplierScope} />,
};

export const activeColumn: HeadCell<IAutoAdjustmentRule> = {
  field: 'active',
  headerName: 'auto-adjustment-rules:fields.active',
  align: CellAlign.CENTER,
  renderCell: (active: boolean) => <ActiveStatusChip value={active} />,
};

export const autoAdjustmentRuleActionColumn: HeadCell<IAutoAdjustmentRule> = {
  field: 'actions',
  headerName: 'common:actions',
  align: CellAlign.CENTER,
  width: 100,
  sortable: false,
  component: AutoAdjustmentRuleActions,
};

export const autoAdjustmentRuleColumns = [
  sourceMetricColumn,
  fromProviderColumn,
  toProviderColumn,
  percentageColumn,
  supplierScopeColumn,
  activeColumn,
  createdATColumn,
  autoAdjustmentRuleActionColumn,
];
